import {
	angleBounds,
	bindInteraction,
	Camera,
	CameraController,
	CameraScript,
	CanvasInputHandler,
	combineBounds,
	combineInteractions,
	dragOrbit,
	Interaction,
	orbit,
	sphereBounds,
	transition,
	View,
	wheelDolly,
	scaleInteraction,
	setView,
	moveTo,
	ControllerBoundsFunc,
	MoveOrder,
	Action,
} from "@animech/camera";
import { assert } from "@animech/core-utilities";
import {
	CameraComponent,
	Color,
	Entity,
	ScriptComponent,
	Vec3,
} from "playcanvas";
import { AutoRenderManager } from "@animech/axiom-scene-utilities";
import { ConfigurationMode } from "./scene-manager";
import { Outline, OutlineAttributes } from "./scripts";
import { positionBounds } from "./camera-bounds";
import { BuildInfo, orthoHeight } from "src/utility";
import { CameraSettings } from "src/common";

export type ViewTransition = "instant" | "smooth";

export type Size = {
	width: number;
	height: number;
};

export type TransitionTarget = {
	view: View;
	orbitCenter: Vec3;
	orbitDistance: number;
};

function getBoundsFuncFromSettings(
	settings: CameraSettings,
): ControllerBoundsFunc {
	return (orbitCenter) => {
		const { allowDolly, allowOrbit, allowPan } = settings;
		const bounds = [
			sphereBounds(orbitCenter, settings.minDistance, "outside"),
			sphereBounds(orbitCenter, settings.maxDistance, "inside"),
		];
		if (allowOrbit) {
			bounds.push(
				angleBounds(
					orbitCenter,
					settings.minElevation,
					settings.maxElevation,
					"vertical",
					"inside",
				),
			);
			if (settings.minAzimuth && settings.maxAzimuth) {
				bounds.push(
					angleBounds(
						orbitCenter,
						settings.minAzimuth,
						settings.maxAzimuth,
						"horizontal",
						"inside",
					),
				);
			}
		}
		if (allowDolly || allowOrbit || allowPan) {
			bounds.push(positionBounds(orbitCenter, settings));
		}
		return combineBounds(...bounds);
	};
}

export class CameraSystem {
	private _camera: Camera;
	private _controller: CameraController;
	private _script: CameraScript;
	private _component: CameraComponent;
	private _scriptComponent: ScriptComponent;
	private _inputHandler: CanvasInputHandler;
	private _settings: CameraSettings;
	private _initialSettings: CameraSettings;
	private _autoRenderManager: AutoRenderManager;
	private _deviceSize: Size;
	private _interactionsEnabled = true;
	private _activeTransitionTarget?: TransitionTarget;
	private _onIsTransitioningChanged: (transitioning: boolean) => void;

	constructor(
		entity: Entity,
		canvas: HTMLCanvasElement,
		deviceSize: Size,
		initialView: View,
		orbitCenter: Vec3,
		settings: CameraSettings,
		autoRenderManager: AutoRenderManager,
		onIsTransitioningChanged: (transitioning: boolean) => void,
	) {
		this._deviceSize = deviceSize;
		this._settings = settings;
		this._initialSettings = { ...settings };
		this._autoRenderManager = autoRenderManager;
		this._onIsTransitioningChanged = onIsTransitioningChanged;
		const scriptComponent = (entity.script ??
			entity.addComponent("script", {})) as ScriptComponent | null;
		assert(scriptComponent !== null, `Failed creating script component`);
		this._camera = new Camera({
			autoReset: true,
			autoRestart: true,
			farClip: settings.farClip,
			nearClip: settings.nearClip,
		});
		this._controller = new CameraController(this._camera);
		this._controller.boundsFunc = getBoundsFuncFromSettings(settings);
		this._controller.orbitCenter = orbitCenter;
		this._camera.view = this._controller
			.boundsFunc(this._controller.orbitCenter)(initialView)
			.setFov(settings.fov);
		this._controller.target = this._camera.view;
		this._controller.motionFunc = (action, view, target) => {
			autoRenderManager.pushAutoRenderRequest();
			this._camera.motionDone.then(autoRenderManager.popAutoRenderRequest);
			if (action.type === "orbit") {
				if (target.projection.type === "orthographic") {
					// Calculate a matching orthographic height for the target view
					// based on the current distance from orbitCenter.
					// Otherwise dollying will have no visible effect to the user.
					target.projection.height = orthoHeight(
						target,
						this._controller.orbitCenter,
					);
				}
				return orbit(view, target, 0.1, this._controller.orbitCenter);
			} else {
				return transition(view, target, 0.1);
			}
		};
		this._script = scriptComponent.create(CameraScript) as CameraScript;
		this._script.camera = this._camera;
		this._scriptComponent = scriptComponent;
		this._inputHandler = new CanvasInputHandler(canvas);
		const component = entity.findComponent("camera") as
			| CameraComponent
			| undefined;
		assert(
			component !== undefined,
			`Camera component not found in entity ${entity.name}`,
		);
		component.clearColor = new Color(0, 0, 0, 0);
		this._component = component;
		this._updateController(deviceSize);
	}

	get view(): View {
		return this._camera.view;
	}

	get isTransitioning(): boolean {
		return !!this._activeTransitionTarget;
	}

	get activeTransitionTarget(): TransitionTarget | undefined {
		return this._activeTransitionTarget;
	}

	get orbitCenter(): Vec3 {
		return this._controller.orbitCenter;
	}

	get orbitDistance(): number {
		return this.orbitCenter.distance(this.view.position);
	}

	get interactionsEnabled(): boolean {
		return this._interactionsEnabled;
	}

	get cameraComponent(): CameraComponent {
		return this._component;
	}

	enableInteractions(enable: boolean) {
		if (enable !== this._interactionsEnabled) {
			this._interactionsEnabled = enable;
			this._updateController(this._deviceSize);
		}
	}

	get settings(): CameraSettings {
		return this._settings;
	}

	updateSettings(value: CameraSettings): CameraSettings {
		const oldSettings = this._settings;
		this._settings = value;
		this._updateController(this._deviceSize);
		return oldSettings;
	}

	createOutline(attributes?: Partial<OutlineAttributes>): Outline {
		return this._scriptComponent.create(Outline, {
			attributes,
		}) as Outline;
	}

	/**
	 * The distance limits are the only settings a configuration mode owns, so
	 * every branch below merges over the *current* settings and restores those
	 * two from the construction-time snapshot on the way back to "product".
	 * Replaying the whole snapshot instead would discard any setting overridden
	 * after construction — a fork that overrides per product mode would silently
	 * get its override undone by a room- or wall-editing round trip.
	 *
	 * If a mode ever overrides a further setting, add it to the "product" branch
	 * as well, or it will leak out of that mode.
	 */
	async setConfigurationMode(configurationMode: ConfigurationMode) {
		if (configurationMode === "room") {
			this.updateSettings({
				...this._settings,
				minDistance: 2,
				maxDistance: 100,
			});
		} else if (configurationMode === "walls") {
			this.updateSettings({
				...this._settings,
				minDistance: 2,
				maxDistance: 50,
			});
		} else {
			this.updateSettings({
				...this._settings,
				minDistance: this._initialSettings.minDistance,
				maxDistance: this._initialSettings.maxDistance,
			});
		}
	}

	async setView(
		view: View,
		orbitCenter: Vec3,
		viewTransition: ViewTransition = "smooth",
		duration = 2,
	): Promise<void> {
		const {
			pushAutoRenderRequest,
			pushSingleFrameRequest,
			popAutoRenderRequest,
		} = this._autoRenderManager;

		this._activeTransitionTarget = undefined;

		if (viewTransition === "instant") {
			setView(this._controller, this._script, view, orbitCenter);
			pushSingleFrameRequest();
		} else {
			const isTransitioningToPerspective =
				this._camera.view.projection.type === "orthographic" &&
				view.projection.type === "perspective";
			if (isTransitioningToPerspective) {
				// Since the camera uses the source view's projection during transition up until
				// the last moment, we switch to using perspective projection before starting the
				// transition to allow the camera to properly lerp between the start- and end distance.
				setView(
					this._controller,
					this._script,
					this._camera.view.setProjection({ type: "perspective" }),
					orbitCenter,
				);
			}
			const orbitDistance = orbitCenter.distance(view.position);
			this._activeTransitionTarget = { view, orbitCenter, orbitDistance };
			pushAutoRenderRequest();
			this._onIsTransitioningChanged(true);
			await moveTo(this._controller, view, orbitCenter, duration);
			this._activeTransitionTarget = undefined;
			this._onIsTransitioningChanged(false);
			popAutoRenderRequest();
		}
	}

	onDeviceResize(deviceSize: Size): void {
		this._updateController(deviceSize);
	}

	private _updateController(deviceSize: Size) {
		this._deviceSize = deviceSize;
		const { allowDolly, allowOrbit, allowPan } = this._settings;
		const interactions: Interaction[] = [];

		if (this._interactionsEnabled) {
			if (allowDolly) {
				interactions.push(scaleInteraction(wheelDolly, 1 / 50));
			}
			if (allowOrbit) {
				interactions.push(scaleInteraction(dragOrbit("primary"), 1 / 10));
			}
			if (allowPan || BuildInfo.mode === "development") {
				interactions.push(this._panTranslate("primary", 1 / 1000));
				interactions.push(this._panTranslate("secondary", 1 / 1000));
			}
		}

		const interaction = combineInteractions(...interactions);
		this._inputHandler.onInput = bindInteraction(interaction, this._controller);
		this._controller.boundsFunc = getBoundsFuncFromSettings(this._settings);
	}

	private _panTranslate =
		(order: MoveOrder, inputScale: number, distanceScale = 1): Interaction =>
		(inputs) => {
			const distance = this._controller.orbitCenter.distance(
				this._camera.view.position,
			);
			const factor = distance * distanceScale * inputScale;
			return inputs.reduce<Action[]>(
				(acc, input) =>
					input.type === "move" &&
					input.axis.type === "pan" &&
					input.axis.order === order
						? [
								...acc,
								input.axis.direction === "horizontal"
									? { type: "translate", x: -input.delta * factor, y: 0, z: 0 }
									: { type: "translate", x: 0, y: input.delta * factor, z: 0 },
							]
						: acc,
				[],
			);
		};
}
